/**
 * STORY#3197 专业版线上购买 —— 纯函数工具
 * 日期 / 价格 / 有效期计算，与 UI 解耦，便于单测与接口接入后复用。
 */
import {
  PLAN_UNIT_DAYS,
  QUALIFY_REASON,
  PAY_COUNTDOWN_DEFAULT_SECONDS,
} from "@/consts/proPlan";

const pad = (n) => (n < 10 ? "0" + n : "" + n);

/**
 * 区域是否未开放线上支付（决定「去升级/去续费」跳套餐页还是专业版权益页）
 * 优先以 reasonCode(41004) 为准；UAT 实测接口在区域关闭时可能**不下发** reasonCode
 * （仅 qualified=false），此时退回 qualified 判断，与老端 isOnlinePaySupported 的
 * `qualified !== false` 保持同一口径，避免误跳套餐页。
 * @param {Object} qualification getCompanyPlanStatus 映射后的对象
 * @returns {boolean}
 */
export function isAreaClosed(qualification) {
  const q = qualification || {};
  const hasReason =
    q.reasonCode !== undefined && q.reasonCode !== null && q.reasonCode !== "";
  if (hasReason) {
    return Number(q.reasonCode) === QUALIFY_REASON.AREA_CLOSED;
  }
  return q.qualified === false;
}

/**
 * 统一转时间戳（毫秒）
 * 接口时间字段规范（payment 服务）：
 * - 日期时间：字符串 `yyyy-MM-dd HH:mm:ss`（如 expireTime、createTime）
 * - 日期：字符串 `yyyy-MM-dd`（如 effectiveTime、entitlementEnd、expireDate）
 * - BaseResp.curDate：服务器毫秒时间戳（number）
 * 此函数同时兼容上述三种以及 Date；无法解析返回 NaN。
 * @param {number|string|Date} v
 * @returns {number} 毫秒时间戳；无法解析返回 NaN
 */
export function toTimestamp(v) {
  if (v === null || v === undefined || v === "") return NaN;
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  const num = Number(v);
  if (!isNaN(num)) return num;
  // "yyyy-MM-dd HH:mm:ss" / "yyyy-MM-dd"：Safari 不支持带空格的横杠格式，需转成斜杠
  return new Date(String(v).replace(/-/g, "/")).getTime();
}

/**
 * 剩余毫秒：以「接口截止时间」与「服务器当前时间」计算，避免客户端时钟偏差
 * 接口未返回服务器时间时退回本地时间。
 * @param {string|number|Date} endTime 截止时间（字符串或时间戳）
 * @param {string|number|Date} [serverNow] BaseResp.curDate
 * @returns {number} 剩余毫秒（最小 0）；无法解析返回 NaN
 */
export function remainMs(endTime, serverNow) {
  const end = toTimestamp(endTime);
  if (isNaN(end)) return NaN;
  const now =
    serverNow !== undefined && serverNow !== null
      ? toTimestamp(serverNow)
      : Date.now();
  return Math.max(0, end - (isNaN(now) ? Date.now() : now));
}

/**
 * 倒计时展示上限：把接口下发的剩余时长压到 PAY_COUNTDOWN_DEFAULT_SECONDS 以内。
 *
 * 背景：后端 expireTime 固定为 30 分钟，PAY_COUNTDOWN_MINUTES 与其一致时
 * 上限不会截断任何值，本函数自动退化为无操作（返回原值）。
 * （2026-09-21 测试期曾把该常量压到 5 分钟以快速联调「订单过期」流程，
 * 2026-09-24 已恢复 30，见 consts/proPlan.js 历史备注。）
 *
 * ⚠️ 注意：仅影响前端「展示值」，不改变后端订单的真实有效期——归零后确认页会先查一次
 * 订单状态（OrderConfirm.handleCountdownEnd），仍为待支付则按服务器时间重新计时，
 * 只有后端确认已取消/关闭才跳「订单已过期」。列表/详情状态始终以后端返回为准。
 * @param {number} ms 接口算得的剩余毫秒
 * @returns {number} 截断后的剩余毫秒
 */
export function capPayCountdown(ms) {
  if (isNaN(ms)) return ms;
  return Math.min(ms, PAY_COUNTDOWN_DEFAULT_SECONDS * 1000);
}

/**
 * 格式化为 yyyy-mm-dd（状态条到期日期用，与老端 proPlanFormatDate 及设计稿 1994:60 一致）
 * 注意：订单列表/订单卡用的是 yyyy.mm.dd（需求 §6），两者勿混用。
 * @param {number|string|Date} date
 * @returns {string}
 */
export function formatDateDash(date) {
  if (!date) return "";
  const ts = toTimestamp(date);
  if (isNaN(ts)) return "";
  const d = new Date(ts);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * 格式化为 yyyy-mm-dd hh:mm
 * 支付成功页用（设计稿 1921:867 的下单时间为「2026-08-25 09:50」）。
 * @param {number|string|Date} date
 * @returns {string}
 */
export function formatDateTimeDash(date) {
  if (!date) return "";
  const ts = toTimestamp(date);
  if (isNaN(ts)) return "";
  const d = new Date(ts);
  return `${formatDateDash(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * 格式化为 yyyy.mm.dd
 * @param {number|string|Date} date
 * @returns {string}
 */
export function formatDateDot(date) {
  if (!date) return "";
  const ts = toTimestamp(date);
  if (isNaN(ts)) return "";
  const d = new Date(ts);
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
}

/**
 * 格式化为 yyyy.mm.dd hh:mm
 * @param {number|string|Date} date
 * @returns {string}
 */
export function formatDateTimeDot(date) {
  if (!date) return "";
  const ts = toTimestamp(date);
  if (isNaN(ts)) return "";
  const d = new Date(ts);
  return `${formatDateDot(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * 剩余天数（向上取整）；now 不传则用当前时间
 * @param {number|string|Date} endTime
 * @param {number|string|Date} [nowTime] 服务器时间
 * @returns {number}
 */
export function remainDays(endTime, nowTime) {
  const end = toTimestamp(endTime);
  const now = nowTime ? toTimestamp(nowTime) : Date.now();
  if (isNaN(end) || isNaN(now)) return 0;
  return Math.ceil((end - now) / 86400000);
}

/**
 * 按天单价 = 总价 ÷ 天数，四舍五入保留 1 位小数（需求 §4）
 * @param {number} price 套餐总价
 * @param {number} days 天数
 * @returns {string} 形如 "2.9"
 */
export function calcUnitPrice(price, days) {
  if (!price || !days) return "0.0";
  return (Number(price) / Number(days)).toFixed(1);
}

/**
 * 套餐时长（月）→ 天数
 * 需求 §4：月 30 / 季 90 / 半年 180 / 年 365。
 * 接口只下发月数（2 位小数），故：12 个月按 365 天，其余按月 × 30 换算。
 * @param {number} months
 * @returns {number}
 */
export function durationToDays(months) {
  const m = Number(months) || 0;
  if (!m) return 0;
  if (m === 12) return PLAN_UNIT_DAYS.year;
  return m * PLAN_UNIT_DAYS.month;
}

/**
 * 套餐时长文案；months 存在时优先按月展示
 * @param {Object} plan { months, days, unitName }
 * @returns {string}
 */
export function planDurationText(plan) {
  if (!plan) return "";
  if (plan.unitName) return plan.unitName;
  return plan.months ? `${plan.months}个月` : "";
}

/**
 * 计算有效期
 *
 * ⚠️ 算法与 **App 端**（`frp_company_app_client` 的
 * `src/views/upgradeToPro/ConfirmOrder.vue` 中 `effectiveDate` / `expireDate`）
 * **逐字对齐**（禅道 #23489）。原先按管理后台 `orderEntryDrawer` 的口径实现，
 * 有两处与 App 不一致、且会导致展示的日期不是用户实际得到的：
 *
 *  1. **生效日期**：付费版（专业版/旗舰版）**未过期**才取「企业到期日 + 1 天」；
 *     **已过期**取当天。原实现不分是否过期、一律取到期日次日，会把已过期企业的
 *     生效日**倒填**到到期日次日（例：08-31 到期、09-23 下单 → 算成 09-01 生效，
 *     白丢 22 天），这正是 #23489 的现象。
 *     免费版 / 无到期日 → 当天。
 *  2. **到期日期** = 生效日 + 时长 − 1 天。原实现写成 `(生效日 − 1 天) + 时长`，
 *     两者在生效日为月初时会差几天（例：生效 3/1、1 个月 → 原实现 3/28，正确应为 3/31）。
 *     加月被月末夹取（如 1/31 + 1 月 → 2/28/29）时**不再减 1 天**。
 *     时长的小数部分按月 30 天折算（如 0.5 个月 → 15 天）。
 *
 * @param {Object} params
 * @param {number} params.months 套餐时长（月，可含小数）
 * @param {Date} params.now 当前日期（默认当天；确认页传服务器时间）
 * @param {Date|string|null} params.currentExpireDate 当前专业版到期日
 * @param {number|null} params.rankType 企业版本 0免费 1专业 2旗舰；
 *   未传时退回「有到期日即视为付费版」的旧判据（保持向后兼容）
 * @returns {{ start: Date, end: Date }}
 */
export function calcValidPeriod({
  months = 1,
  now = new Date(),
  currentExpireDate = null,
  rankType = null,
} = {}) {
  const today = startOfDay(now);
  const expire = parseDateOnly(currentExpireDate);
  // 是否付费版：优先用接口下发的 rankType；未传时按「有到期日」推断
  const isPaid =
    rankType === null || rankType === undefined
      ? !!expire
      : Number(rankType) >= 1;
  // 生效日：付费版且「到期日不早于今天」→ 到期日次日（可与今天相同或更晚）；
  // 已过期 / 免费版 / 无到期日 → 当天
  const start =
    isPaid && expire && !isBeforeDay(expire, today)
      ? addDays(expire, 1)
      : today;
  // 到期日 = 生效日 + 时长 − 1 天
  const total = Number(months) || 0;
  if (!total) return { start, end: start };
  const whole = Math.trunc(total);
  const extraDays = Math.round((total - whole) * 30);
  let end = addMonths(start, whole);
  if (extraDays) end = addDays(end, extraDays);
  // 加月时被月末夹取（日期变了）→ 不再减 1 天
  const isMonthEndClamped = !extraDays && end.getDate() !== start.getDate();
  if (!isMonthEndClamped) end = addDays(end, -1);
  return { start, end };
}

/** a 是否早于 b（按「天」比较） */
function isBeforeDay(a, b) {
  return startOfDay(a).getTime() < startOfDay(b).getTime();
}

/** 当天 0 点（避免时分秒参与比较与运算） */
function startOfDay(date) {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** 解析 yyyy-MM-dd（或可被 Date 解析的值）为本地日期；无效返回 null */
function parseDateOnly(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : startOfDay(value);
  }
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(value));
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const ts = toTimestamp(value);
  return isNaN(ts) ? null : startOfDay(new Date(ts));
}

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * 自然月相加（与管理后台 `addMonths` 逐字对齐）：
 * 日超出目标月天数时取该月最后一天；小数部分按月 30 天折算。
 */
function addMonths(date, months) {
  const whole = Math.trunc(months);
  const fraction = months - whole;
  const day = date.getDate();
  const result = new Date(date.getFullYear(), date.getMonth() + whole, 1);
  const lastDay = new Date(
    result.getFullYear(),
    result.getMonth() + 1,
    0
  ).getDate();
  result.setDate(Math.min(day, lastDay));
  if (fraction) {
    result.setDate(result.getDate() + Math.round(fraction * 30));
  }
  return result;
}

/**
 * 协议预览页地址（term_service.html 由老端提供，与页面同源）
 * 用当前 origin 拼接，自动区分本地/测试/线上；不可硬编码正式服域名。
 * @param {number} type AGREEMENT_TYPE: 1 用户协议 / 2 隐私政策 / 20 专业版服务协议
 * @returns {string}
 */
export function agreementUrl(type) {
  const origin = window.location.origin;
  return `${origin}/term_service.html${type ? "?" + type : ""}`;
}

export { PLAN_UNIT_DAYS };
